import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './AuthContext'
import platformSettingsService from '../services/platformSettings'

const LanguageContext = createContext(null)

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)' }
]

// English is the base language and is always available, even if the
// enabled-languages fetch fails.
const BASE_LANGUAGE = 'en'

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation()
  const { user, profile } = useAuth()

  // Codes enabled platform-wide (#206). Default to all known codes so the
  // selector is fully populated until the platform config resolves.
  const [enabledCodes, setEnabledCodes] = useState(
    SUPPORTED_LANGUAGES.map((l) => l.code)
  )

  // Fetch the platform-wide enabled-languages list (public endpoint).
  useEffect(() => {
    let cancelled = false
    platformSettingsService
      .getEnabledLanguages()
      .then((codes) => {
        if (cancelled || !Array.isArray(codes) || codes.length === 0) return
        // Always keep English available as a fallback.
        const next = codes.includes(BASE_LANGUAGE) ? codes : [BASE_LANGUAGE, ...codes]
        setEnabledCodes(next)
      })
      .catch(() => {
        // Fail open: keep the full list so the UI is never left without languages.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // If the active language has been disabled platform-wide, fall back to English.
  useEffect(() => {
    if (i18n.language && !enabledCodes.includes(i18n.language)) {
      i18n.changeLanguage(BASE_LANGUAGE)
      localStorage.setItem('preferredLanguage', BASE_LANGUAGE)
    }
  }, [enabledCodes, i18n])

  // Sync language from user profile on login (only if that language is enabled).
  useEffect(() => {
    if (user && profile?.preferredLanguage && enabledCodes.includes(profile.preferredLanguage)) {
      if (i18n.language !== profile.preferredLanguage) {
        i18n.changeLanguage(profile.preferredLanguage)
        localStorage.setItem('preferredLanguage', profile.preferredLanguage)
      }
    }
  }, [user, profile?.preferredLanguage, enabledCodes, i18n])

  const changeLanguage = useCallback(async (languageCode) => {
    // Update i18next immediately
    await i18n.changeLanguage(languageCode)

    // Update localStorage
    localStorage.setItem('preferredLanguage', languageCode)

    return languageCode
  }, [i18n])

  const currentLanguage = i18n.language || 'en'

  // Only expose languages that are enabled platform-wide.
  const supportedLanguages = SUPPORTED_LANGUAGES.filter((l) =>
    enabledCodes.includes(l.code)
  )

  const value = {
    currentLanguage,
    changeLanguage,
    supportedLanguages,
    enabledLanguageCodes: enabledCodes,
    isLoading: !i18n.isInitialized
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export default LanguageContext
